import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FounderConfirmPublish } from "../../../components/founder/FounderConfirmPublish";
import { FounderIconButton } from "../../../components/founder/FounderIconButton";
import {
  FounderOperationalShell,
  FounderSplitPanel,
} from "../../../components/founder/FounderOperationalShell";
import {
  FounderEmptyState,
  FounderErrorState,
  FounderLoadingState,
} from "../../../components/founder/FounderWorkspaceChrome";
import {
  fetchFounderApplicationDetail,
  fetchFounderApplications,
  fetchFounderWorkspaceCounts,
  submitFounderApplicationReview,
  type FounderApplicationDetail,
  type FounderApplicationRow,
  type FounderReviewDecision,
} from "../../../founder/founderWorkspaceApi";

const TABS = [
  { id: "pending", label: "Pending Reviews" },
  { id: "published", label: "Published" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All Apps" },
] as const;

const STATUS_BY_TAB: Record<string, string | undefined> = {
  pending: "pending_review",
  published: "published",
  rejected: "rejected",
  all: undefined,
};

function formatStatus(status: string): string {
  return status.replaceAll("_", " ");
}

export function FounderAppsWorkspace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") ?? "pending";
  const selectedAppId = searchParams.get("app");
  const searchQuery = searchParams.get("q") ?? "";

  const [counts, setCounts] = useState<Awaited<ReturnType<typeof fetchFounderWorkspaceCounts>> | null>(null);
  const [apps, setApps] = useState<FounderApplicationRow[]>([]);
  const [selectedApp, setSelectedApp] = useState<FounderApplicationRow | null>(null);
  const [appDetail, setAppDetail] = useState<FounderApplicationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingDecision, setPendingDecision] = useState<FounderReviewDecision | null>(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionResult, setActionResult] = useState("");

  const statusFilter = STATUS_BY_TAB[tab];

  useEffect(() => {
    fetchFounderWorkspaceCounts().then(setCounts).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchFounderApplications(statusFilter, searchQuery.trim() || undefined)
      .then((rows) => {
        if (!cancelled) {
          setApps(rows);
          setError("");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Could not load applications.");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    if (!selectedAppId) {
      setSelectedApp(null);
      setAppDetail(null);
      return;
    }
    setSelectedApp(apps.find((app) => app.id === selectedAppId) ?? null);
    fetchFounderApplicationDetail(selectedAppId)
      .then(setAppDetail)
      .catch(() => setAppDetail(null));
  }, [selectedAppId, apps]);

  const availableActions = useMemo(() => {
    if (!selectedApp) {
      return [] as FounderReviewDecision[];
    }
    let actions: FounderReviewDecision[] = [];
    switch (selectedApp.status) {
      case "pending_review":
      case "revision_requested":
        actions = ["approve", "request_revision", "reject"];
        break;
      case "published":
        actions = ["suspend", "delist"];
        break;
      case "suspended":
        actions = ["restore", "delist"];
        break;
      default:
        actions = [];
    }
    if (
      actions.includes("approve") &&
      appDetail &&
      !appDetail.reviewReadiness.canApprove
    ) {
      return actions.filter((action) => action !== "approve");
    }
    return actions;
  }, [selectedApp, appDetail]);

  function setTab(nextTab: string) {
    const next = new URLSearchParams(searchParams);
    next.set("tab", nextTab);
    next.delete("app");
    setSearchParams(next, { replace: true });
  }

  function openApp(appId: string) {
    const next = new URLSearchParams(searchParams);
    next.set("app", appId);
    setSearchParams(next, { replace: true });
    setPendingDecision(null);
    setActionResult("");
  }

  function closeApp() {
    const next = new URLSearchParams(searchParams);
    next.delete("app");
    setSearchParams(next, { replace: true });
  }

  async function executeDecision() {
    if (!pendingDecision || !selectedApp) {
      return;
    }
    setSubmitting(true);
    try {
      const nextStatus = await submitFounderApplicationReview(
        selectedApp.id,
        pendingDecision,
        notes.trim() || undefined,
      );
      setActionResult(`Updated to ${formatStatus(nextStatus)}.`);
      setSelectedApp({ ...selectedApp, status: nextStatus });
      setApps((rows) =>
        rows.map((row) => (row.id === selectedApp.id ? { ...row, status: nextStatus } : row)),
      );
      setPendingDecision(null);
      setNotes("");
    } catch {
      setActionResult("Action failed. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  const metrics = counts
    ? [
        { id: "p", label: "Pending", value: counts.applicationsPendingReview, tone: "coral" as const },
        { id: "pub", label: "Published", value: counts.publishedApplications, tone: "mint" as const },
        { id: "rev", label: "Awaiting revision", value: counts.applicationsAwaitingRevision, tone: "sand" as const },
        { id: "sus", label: "Suspended", value: counts.suspendedApplications, tone: "steel" as const },
      ]
    : [];

  const toolbar = (
    <input
      aria-label="Search applications"
      className="oac-founder-ops-search"
      onChange={(e) => {
        const next = new URLSearchParams(searchParams);
        if (e.target.value) {
          next.set("q", e.target.value);
        } else {
          next.delete("q");
        }
        setSearchParams(next, { replace: true });
      }}
      placeholder="Search apps or publishers"
      type="search"
      value={searchQuery}
    />
  );

  return (
    <FounderOperationalShell
      activeTab={tab}
      metrics={metrics}
      onTabChange={setTab}
      tabs={[...TABS]}
      title="Apps & Reviews"
      toolbar={toolbar}
    >
      <FounderSplitPanel
        onCloseSecondary={closeApp}
        primary={
          <>
            {loading ? <FounderLoadingState /> : null}
            {!loading && error ? <FounderErrorState message={error} /> : null}
            {!loading && !error && apps.length === 0 ? (
              <FounderEmptyState message="No applications in this view." />
            ) : null}
            {!loading && !error && apps.length > 0 ? (
              <div className="oac-founder-table-wrap">
                <table className="oac-founder-table">
                  <thead>
                    <tr>
                      <th>App</th>
                      <th>Publisher</th>
                      <th>Status</th>
                      <th aria-label="Actions"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map((app) => (
                      <tr className={selectedAppId === app.id ? "oac-founder-table__row--active" : ""} key={app.id}>
                        <td>{app.name}</td>
                        <td>{app.publisher_name}</td>
                        <td>
                          <span className="oac-founder-badge oac-founder-badge--neutral">
                            {formatStatus(app.status)}
                          </span>
                        </td>
                        <td>
                          <FounderIconButton
                            kind="view"
                            label={`Inspect ${app.name}`}
                            onClick={() => openApp(app.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        }
        secondary={
          selectedApp ? (
            <div className="oac-founder-ops-detail">
              <p className="oac-founder-ops-detail__meta">{selectedApp.publisher_name}</p>
              <p className="oac-founder-ops-detail__meta">
                Status: <strong>{formatStatus(selectedApp.status)}</strong>
              </p>
              <p className="oac-founder-ops-detail__meta">
                Category: {selectedApp.category_name ?? "—"}
              </p>

              {appDetail ? (
                <>
                  <p className="oac-founder-ops-detail__meta">
                    Security: {String(appDetail.reviewReadiness.securityStatus)}
                  </p>
                  {appDetail.reviewReadiness.blockingReason ? (
                    <p className="oac-founder-ops-footnote">{appDetail.reviewReadiness.blockingReason}</p>
                  ) : null}
                  {appDetail.securityScans.length > 0 ? (
                    <ul className="oac-founder-ops-list oac-founder-ops-list--compact">
                      {appDetail.securityScans.slice(0, 3).map((scan) => (
                        <li className="oac-founder-ops-list__item" key={String(scan.id)}>
                          <div>
                            <span className="oac-founder-ops-list__title">
                              {String(scan.verdict ?? scan.result ?? "scan")}
                            </span>
                            <span className="oac-founder-ops-list__meta">
                              {String(scan.reason ?? scan.threat_level ?? "—")}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="oac-founder-ops-panel__empty">No security scan evidence available.</p>
                  )}
                </>
              ) : (
                <p className="oac-founder-ops-panel__empty">Loading security evidence...</p>
              )}

              {availableActions.length > 0 ? (
                <div className="oac-founder-ops-detail__actions">
                  {availableActions.map((decision) => (
                    <button
                      className={
                        decision === "reject" || decision === "delist"
                          ? "oac-founder-btn oac-founder-btn--danger"
                          : "oac-founder-btn oac-founder-btn--primary"
                      }
                      key={decision}
                      onClick={() => setPendingDecision(decision)}
                      type="button"
                    >
                      {formatStatus(decision)}
                    </button>
                  ))}
                </div>
              ) : null}

              {pendingDecision ? (
                <FounderConfirmPublish
                  message={`Confirm ${formatStatus(pendingDecision)}?`}
                  onCancel={() => setPendingDecision(null)}
                  onConfirm={executeDecision}
                  submitting={submitting}
                >
                  <textarea
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notes (optional)"
                    rows={3}
                    value={notes}
                  />
                </FounderConfirmPublish>
              ) : null}

              {actionResult ? <p className="oac-founder-ops-detail__result">{actionResult}</p> : null}
            </div>
          ) : undefined
        }
        secondaryTitle={selectedApp?.name}
      />
    </FounderOperationalShell>
  );
}
